import React, { useCallback, useRef, useState } from "react";
import axios from "axios";
import { BsArrowLeft } from "react-icons/bs";
import { IoDocumentTextOutline, IoTrashOutline } from "react-icons/io5";
import { TbFileUpload } from "react-icons/tb";
import useKycDetails from "../../../hooks/useKycDetails";
import useLimits from "../../../hooks/useLimits";
import useAuthAxios from "../../../utils/baseAxios";
import { API } from "../../../constants/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { errorMessage } from "../../../utils/errorMessage";
import ClipLoader from "react-spinners/ClipLoader";
import Webcam from "react-webcam";

const DOCUMENT_ORIGIN = "Nigeria";
const UTILITY_CATEGORY = "Utility Bill";

const ACCEPT_IMAGE_OR_PDF =
  "image/png,image/jpeg,image/jpg,image/gif,image/webp,application/pdf";
const ACCEPT_IMAGE_ONLY = "image/png,image/jpeg,image/jpg,image/gif,image/webp";

const isAllowedDocument = (file: File, allowPdf: boolean): boolean => {
  const isImage = file.type.startsWith("image/");
  const isPdf = file.type === "application/pdf";
  return isImage || (allowPdf && isPdf);
};

const DOCUMENT_OPTIONS = [
  "Driver's License",
  "International Passport",
  "Digital NIN Slip",
  "NIN ID Card",
  "NIN Slip",
  "Voter's Card",
];

const Level2Flow = ({
  setOpenLevel2Flow,
  setOpenKyc,
  setOpenKycOnboarding,
}: {
  setOpenLevel2Flow: (open: boolean) => void;
  setOpenKyc?: (open: boolean) => void;
  setOpenKycOnboarding?: (open: boolean) => void;
}) => {
  const queryClient = useQueryClient();
  const axiosInstance = useAuthAxios();
  const { limits } = useLimits();
  const { data: kycDetailsData } = useKycDetails();
  const details = kycDetailsData?.kycDetails;
  const hasLevel1Liveness = !!details?.first_stage?.smile_id_data?.imageUrl;
  const LEVEL_2 = limits[1];

  const [document, setDocument] = useState("");
  const [govNumber, setGovNumber] = useState("");
  const [govIdFile, setGovIdFile] = useState<File | null>(null);
  const [govSignedUrl, setGovSignedUrl] = useState("");
  const [govObjectUrl, setGovObjectUrl] = useState("");
  const [houseNo, setHouseNo] = useState("");
  const [houseAddress, setHouseAddress] = useState("");
  const [houseLandmark, setHouseLandmark] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [addressFile, setAddressFile] = useState<File | null>(null);
  const [addressSignedUrl, setAddressSignedUrl] = useState("");
  const [addressObjectUrl, setAddressObjectUrl] = useState("");
  const [livenessFile, setLivenessFile] = useState<File | null>(null);
  const [livenessSignedUrl, setLivenessSignedUrl] = useState("");
  const [livenessObjectUrl, setLivenessObjectUrl] = useState("");
  const [livenessUploaded, setLivenessUploaded] = useState(false);
  const [showLivenessWebcam, setShowLivenessWebcam] = useState(false);
  const webcamRef = useRef<Webcam | null>(null);
  const pendingGovFileRef = useRef<File | null>(null);
  const pendingAddressFileRef = useRef<File | null>(null);

  const getUploadUrl = async (data: { documentType: string }) => {
    const response = await axiosInstance.post(API.getKyc3UploadURL, data);
    return response.data;
  };

  const getGovUrl = useMutation({
    mutationFn: () => getUploadUrl({ documentType: "governmentID" }),
    onSuccess: (r: { data: { objectURL: string; signedURL: string } }) => {
      setGovObjectUrl(r.data.objectURL);
      setGovSignedUrl(r.data.signedURL);
      const file = pendingGovFileRef.current;
      if (file) {
        uploadGovFile.mutate({ file, signedUrl: r.data.signedURL });
        pendingGovFileRef.current = null;
      }
    },
    onError: (e: any) => {
      pendingGovFileRef.current = null;
      toast.error(errorMessage(e?.message || e?.data?.message));
    },
  });

  const uploadGovFile = useMutation({
    mutationFn: async ({
      file,
      signedUrl,
    }: {
      file: File;
      signedUrl: string;
    }) => {
      await axios.put(signedUrl, file, {
        headers: { "Content-Type": file.type },
      });
    },
    onSuccess: () => {
      toast.success("ID document uploaded");
    },
    onError: (e: any) => {
      toast.error(errorMessage(e?.message || e?.data?.message));
    },
  });

  const getAddressUrl = useMutation({
    mutationFn: () => getUploadUrl({ documentType: "utilityBill" }),
    onSuccess: (r: { data: { objectURL: string; signedURL: string } }) => {
      setAddressObjectUrl(r.data.objectURL);
      setAddressSignedUrl(r.data.signedURL);
      const file = pendingAddressFileRef.current;
      if (file) {
        uploadAddressFile.mutate({ file, signedUrl: r.data.signedURL });
        pendingAddressFileRef.current = null;
      }
    },
    onError: (e: any) => {
      pendingAddressFileRef.current = null;
      toast.error(errorMessage(e?.message || e?.data?.message));
    },
  });

  const uploadAddressFile = useMutation({
    mutationFn: async ({
      file,
      signedUrl,
    }: {
      file: File;
      signedUrl: string;
    }) => {
      await axios.put(signedUrl, file, {
        headers: { "Content-Type": file.type },
      });
    },
    onSuccess: () => {
      toast.success("Address proof uploaded");
    },
    onError: (e: any) => {
      toast.error(errorMessage(e?.message || e?.data?.message));
    },
  });

  const getLivenessUrl = useMutation({
    mutationFn: () => getUploadUrl({ documentType: "livelinessCheck" }),
    onSuccess: (r: { data: { objectURL: string; signedURL: string } }) => {
      setLivenessObjectUrl(r.data.objectURL);
      setLivenessSignedUrl(r.data.signedURL);
    },
    onError: (e: any) => {
      toast.error(errorMessage(e?.message || e?.data?.message));
    },
  });

  const uploadLivenessFile = useMutation({
    mutationFn: async ({
      file,
      signedUrl,
    }: {
      file: File;
      signedUrl: string;
    }) => {
      await axios.put(signedUrl, file, {
        headers: { "Content-Type": file.type },
      });
    },
    onSuccess: () => {
      setLivenessUploaded(true);
      toast.success("Selfie uploaded");
    },
    onError: (e: any) => {
      setLivenessUploaded(false);
      toast.error(errorMessage(e?.message || e?.data?.message));
    },
  });

  const submitLevel2 = useMutation({
    mutationFn: async (data: any) => {
      const response = await axiosInstance.post(API.submitKyc3, data);
      return response.data;
    },
    onSuccess: (r: { message?: string }) => {
      toast.success(r?.message ?? "Submitted for review");
      queryClient.invalidateQueries({ queryKey: ["get-kyc-details"] });
      setOpenLevel2Flow(false);
      setOpenKyc?.(false);
      setOpenKycOnboarding?.(false);
    },
    onError: (e: any) => {
      toast.error(errorMessage(e?.message || e?.data?.message));
    },
  });

  const handleGovUpload = (file: File) => {
    setGovIdFile(file);
    if (govSignedUrl) {
      uploadGovFile.mutate({ file, signedUrl: govSignedUrl });
    } else {
      pendingGovFileRef.current = file;
      getGovUrl.mutate(undefined);
    }
  };

  const handleAddressUpload = (file: File) => {
    setAddressFile(file);
    if (addressSignedUrl) {
      uploadAddressFile.mutate({ file, signedUrl: addressSignedUrl });
    } else {
      pendingAddressFileRef.current = file;
      getAddressUrl.mutate(undefined);
    }
  };

  const handleLivenessCapture = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        const byteString = atob(imageSrc.split(",")[1]);
        const mimeString = imageSrc.split(",")[0].split(":")[1].split(";")[0];
        const arrayBuffer = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i++) {
          arrayBuffer[i] = byteString.charCodeAt(i);
        }
        const file = new File([arrayBuffer], "selfie.jpg", {
          type: mimeString,
        });
        setLivenessFile(file);
        if (livenessSignedUrl) {
          uploadLivenessFile.mutate({ file, signedUrl: livenessSignedUrl });
        } else {
          getLivenessUrl.mutate(undefined, {
            onSuccess: (r: { data: { signedURL: string } }) => {
              uploadLivenessFile.mutate({
                file,
                signedUrl: r.data.signedURL,
              });
            },
          });
        }
      }
      setShowLivenessWebcam(false);
    }
  }, [livenessSignedUrl]);

  const handleLivenessFileSelect = (file: File) => {
    setLivenessFile(file);
    if (livenessSignedUrl) {
      uploadLivenessFile.mutate({ file, signedUrl: livenessSignedUrl });
    } else {
      getLivenessUrl.mutate(undefined, {
        onSuccess: (r: { data: { signedURL: string } }) => {
          uploadLivenessFile.mutate({
            file,
            signedUrl: r.data.signedURL,
          });
        },
      });
    }
  };

  const canSubmit =
    document &&
    govNumber &&
    govObjectUrl &&
    houseNo &&
    houseAddress &&
    houseLandmark &&
    city &&
    state &&
    postalCode &&
    addressObjectUrl &&
    (hasLevel1Liveness || livenessObjectUrl);

  const handleSubmit = () => {
    if (!canSubmit) return;
    const payload: any = {
      documentOrigin: DOCUMENT_ORIGIN,
      governmentID: {
        category: document,
        imageURL: govObjectUrl,
        number: govNumber,
      },
      proofOfAddress: {
        houseAddress: {
          apartmentNumber: houseNo,
          streetName: houseAddress,
          landmark: houseLandmark,
          city,
          state,
          postalCode,
        },
        utilityBill: {
          category: UTILITY_CATEGORY,
          imageURL: addressObjectUrl,
        },
      },
    };
    if (!hasLevel1Liveness && livenessObjectUrl) {
      payload.livelinessCheck = { imageURL: livenessObjectUrl };
    }
    submitLevel2.mutate(payload);
  };

  return (
    <div className="fixed inset-0 flex font-manrope justify-center items-start pt-16 pb-16 bg-[#E7EBEE] dark:bg-primary_dark dark:bg-opacity-95 backdrop-blur-sm">
      <div className="w-11/12 mds:w-8/12 md:7/12 border dark:border-[#303030] bg-white rounded-xl mx-auto dark:bg-[#192025] lgss:w-2/5 xxl:w-1/3 flex flex-col max-h-[85vh] overflow-hidden">
        <div className="shrink-0 p-6 pb-0">
          <div className="w-full flex justify-start gap-6 items-center">
            <button
              onClick={() => setOpenLevel2Flow(false)}
              className="flex items-center justify-center"
            >
              <BsArrowLeft className="text-black dark:text-white text-[20px]" />
            </button>
            <h4 className="text-gray-900 dark:text-gray-100 font-sora font-medium text-[20px]">
              Level 2 Verification
            </h4>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto min-h-0 p-6 pt-4">
          <h5 className="text-gray-900 dark:text-gray-100 font-sora font-bold text-[20px] mt-2">
            What you&apos;ll unlock
          </h5>
          <div className="mt-3 rounded-xl border border-gray-200 bg-[#E8F3FC] dark:bg-[#1C2227] dark:border-gray-600 p-4">
            <div className="flex items-center gap-2 justify-between">
              <h5 className="text-gray-900 dark:text-gray-100 font-semibold text-[15px]">
                Level 2
              </h5>
              <span className=" text-gray-500 text-[11px] font-bold uppercase ">
                CURRENT LEVEL
              </span>
            </div>
            <div className="mt-3 space-y-4 bg-white dark:bg-[#1C2227] p-4 rounded-xl">
              <div className="flex justify-between border-b border-gray-200 dark:border-gray-600 pb-2 text-[13px]">
                <span className="text-gray-900 dark:text-gray-100 font-medium text-[14px]">
                  Daily Trading Limit
                </span>
                <div className="flex flex-col">
                  <span className="text-gray-900 dark:text-gray-100 text-right font-bold text-[20px]">
                    <span className="text-[14px]">$</span>
                    {LEVEL_2.tradingRaw.toLocaleString()}
                  </span>
                  {/* <span className="text-gray-500 text-right mt-1 dark:text-gray-400  text-[12px]">
                    ${TRADING_LEFT.toLocaleString()} left
                  </span> */}
                </div>
              </div>
              <div className="flex justify-between text-[13px]">
                <span className="text-gray-900 dark:text-gray-100 font-medium text-[14px]">
                  Daily Wallet Withdrawal
                </span>
                <div className="flex flex-col">
                  <span className="text-gray-900 dark:text-gray-100 text-right font-bold text-[20px]">
                    <span className="text-[14px]">$</span>
                    {LEVEL_2.withdrawalRaw.toLocaleString()}
                  </span>
                  {/* <span className="text-gray-500 text-right mt-1 dark:text-gray-400  text-[12px]">
                    ${WITHDRAWAL_LEFT.toLocaleString()} left
                  </span> */}
                </div>
              </div>
            </div>
          </div>

          <h5 className="text-gray-900 dark:text-gray-100 font-sora font-bold text-[20px] mt-6">
            Requirements
          </h5>
          <div className="mt-3 space-y-3">
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-600 dark:bg-[#1C2227]">
              <div className="flex items-center gap-2 flex-wrap">
                <h5 className="text-gray-900 dark:text-gray-100 font-semibold text-[14px]">
                  Government ID
                </h5>
                <span className="px-2 py-0.5 bg-[#FDEDDB] dark:bg-[#F28A0F]/20 text-[#F28A0F] dark:text-[#E8A84A] text-[9px] font-bold uppercase rounded">
                  REQUIRED
                </span>
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-[13px] mt-1">
                NIN, Passport, or Driver&apos;s License.
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-600 dark:bg-[#1C2227]">
              <div className="flex items-center gap-2 flex-wrap">
                <h5 className="text-gray-900 dark:text-gray-100 font-semibold text-[14px]">
                  Address Verification
                </h5>
                <span className="px-2 py-0.5 bg-[#FDEDDB] dark:bg-[#F28A0F]/20 text-[#F28A0F] dark:text-[#E8A84A] text-[9px] font-bold uppercase rounded">
                  REQUIRED
                </span>
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-[13px] mt-1">
                Utility bill or bank statement.
              </p>
            </div>
            {!hasLevel1Liveness && (
              <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-600 dark:bg-[#1C2227]">
                <div className="flex items-center gap-2 flex-wrap">
                  <h5 className="text-gray-900 dark:text-gray-100 font-semibold text-[14px]">
                    Liveness Check
                  </h5>
                  <span className="px-2 py-0.5 bg-[#FDEDDB] dark:bg-[#F28A0F]/20 text-[#F28A0F] dark:text-[#E8A84A] text-[9px] font-bold uppercase rounded">
                    REQUIRED
                  </span>
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-[13px] mt-1">
                  Take a selfie for verification.
                </p>
              </div>
            )}
          </div>

          <div className="mt-6">
            <label className="text-gray-900 dark:text-gray-100 font-semibold text-[14px]">
              Select ID
            </label>
            <select
              value={document}
              onChange={(e) => setDocument(e.target.value)}
              className="w-full h-[52px] mt-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#1C2227] text-gray-900 dark:text-gray-100 px-4 text-[14px] outline-none appearance-none bg-no-repeat bg-right pr-10"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundSize: "24px",
                backgroundPosition: "right 8px center",
              }}
            >
              <option value="">Choose document type</option>
              {DOCUMENT_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            {document && (
              <input
                type="text"
                inputMode="numeric"
                placeholder={`Enter your ${document} number`}
                value={govNumber}
                onChange={(e) =>
                  setGovNumber(e.target.value.replace(/\D/g, ""))
                }
                className="w-full h-[52px] mt-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#1C2227] text-gray-900 dark:text-gray-100 px-4 text-[14px] outline-none"
              />
            )}
          </div>

          <div className="mt-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-600 dark:bg-[#1C2227]">
            <div className="flex items-center gap-3">
              <IoDocumentTextOutline className="text-gray-500 dark:text-gray-400 text-[24px] shrink-0" />
              <div className="min-w-0 flex-1">
                <h5 className="text-gray-900 dark:text-gray-100 font-semibold text-[14px]">
                  {govIdFile ? govIdFile.name : "Upload ID Document"}
                </h5>
                <p className="text-gray-500 dark:text-gray-400 text-[12px] mt-0.5">
                  {govIdFile ? "Change ID document" : "Clear photo of your ID"}
                </p>
              </div>
              {govIdFile ? (
                <div className="shrink-0 flex items-center gap-2">
                  {uploadGovFile.isPending && (
                    <ClipLoader size={20} color="#0B75C2" />
                  )}

                  <button
                    onClick={() => {
                      setGovIdFile(null);
                      setGovObjectUrl("");
                      setGovSignedUrl("");
                    }}
                    className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
                    aria-label="Remove file"
                  >
                    <IoTrashOutline className="text-[18px]" />
                  </button>
                </div>
              ) : (
                <label className="shrink-0 w-10 h-10 rounded-full border-2 border-dashed border-gray-300 dark:border-gray-500 flex items-center justify-center cursor-pointer hover:border-secondary_button">
                  <TbFileUpload className="text-gray-500 dark:text-gray-400 text-[20px]" />
                  <input
                    type="file"
                    accept={ACCEPT_IMAGE_OR_PDF}
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      if (!f) return;
                      if (!isAllowedDocument(f, true)) {
                        toast.error(
                          "Please upload an image (PNG, JPG, etc.) or PDF only."
                        );
                        return;
                      }
                      handleGovUpload(f);
                    }}
                  />
                </label>
              )}
            </div>
          </div>

          <div className="mt-4 space-y-3">
            <label className="text-gray-900 dark:text-gray-100 font-semibold text-[14px]">
              Address details
            </label>
            <div className="grid grid-cols-1 gap-2">
              <input
                type="text"
                placeholder="Apartment / House No"
                value={houseNo}
                onChange={(e) => setHouseNo(e.target.value)}
                className="h-[44px] rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#1C2227] text-gray-900 dark:text-gray-100 px-4 text-[13px] outline-none"
              />
              <input
                type="text"
                placeholder="Street address"
                value={houseAddress}
                onChange={(e) => setHouseAddress(e.target.value)}
                className="h-[44px] rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#1C2227] text-gray-900 dark:text-gray-100 px-4 text-[13px] outline-none"
              />
              <input
                type="text"
                placeholder="Landmark"
                value={houseLandmark}
                onChange={(e) => setHouseLandmark(e.target.value)}
                className="h-[44px] rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#1C2227] text-gray-900 dark:text-gray-100 px-4 text-[13px] outline-none"
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  placeholder="City"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="h-[44px] rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#1C2227] text-gray-900 dark:text-gray-100 px-4 text-[13px] outline-none"
                />
                <input
                  type="text"
                  placeholder="State"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="h-[44px] rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#1C2227] text-gray-900 dark:text-gray-100 px-4 text-[13px] outline-none"
                />
              </div>
              <input
                type="text"
                placeholder="Postal code"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                className="h-[44px] rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#1C2227] text-gray-900 dark:text-gray-100 px-4 text-[13px] outline-none"
              />
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-600 dark:bg-[#1C2227]">
            <div className="flex items-center gap-3">
              <IoDocumentTextOutline className="text-gray-500 dark:text-gray-400 text-[24px] shrink-0" />
              <div className="min-w-0 flex-1">
                <h5 className="text-gray-900 dark:text-gray-100 font-semibold text-[14px]">
                  {addressFile ? addressFile.name : "Upload Address Proof"}
                </h5>
                <p className="text-gray-500 dark:text-gray-400 text-[12px] mt-0.5 truncate">
                  {addressFile ? "Change address proof" : "Upload utility bill"}
                </p>
              </div>
              {addressFile ? (
                <div className="shrink-0 flex items-center gap-2">
                  {uploadAddressFile.isPending ? (
                    <ClipLoader size={20} color="#0B75C2" />
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setAddressFile(null);
                        setAddressObjectUrl("");
                        setAddressSignedUrl("");
                        setAddressObjectUrl("");
                      }}
                      className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
                      aria-label="Remove file"
                    >
                      <IoTrashOutline className="text-[18px]" />
                    </button>
                  )}
                </div>
              ) : (
                <label className="shrink-0 w-10 h-10 rounded-full border-2 border-dashed border-gray-300 dark:border-gray-500 flex items-center justify-center cursor-pointer hover:border-secondary_button">
                  <TbFileUpload className="text-gray-500 dark:text-gray-400 text-[20px]" />
                  <input
                    type="file"
                    accept={ACCEPT_IMAGE_OR_PDF}
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      if (!f) return;
                      if (!isAllowedDocument(f, true)) {
                        toast.error(
                          "Please upload an image (PNG, JPG, etc.) or PDF only."
                        );
                        return;
                      }
                      setAddressFile(f);
                      handleAddressUpload(f);
                    }}
                  />
                </label>
              )}
            </div>
          </div>

          {!hasLevel1Liveness && (
            <div className="mt-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-600 dark:bg-[#1C2227]">
              <h5 className="text-gray-900 dark:text-gray-100 font-semibold text-[14px]">
                Liveness Check
              </h5>
              <p className="text-gray-500 dark:text-gray-400 text-[12px] mt-1">
                Take a selfie for verification
              </p>
              {showLivenessWebcam ? (
                <div className="mt-4">
                  <Webcam
                    ref={webcamRef}
                    audio={false}
                    screenshotFormat="image/jpeg"
                    videoConstraints={{ facingMode: "user" }}
                    className="w-full max-w-sm rounded-lg"
                  />
                  <div className="flex gap-2 mt-2">
                    <button
                      type="button"
                      onClick={handleLivenessCapture}
                      className="flex-1 h-[44px] rounded-xl bg-secondary_button text-white text-[14px] font-medium"
                    >
                      Capture
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowLivenessWebcam(false)}
                      className="flex-1 h-[44px] rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-[14px]"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : livenessFile ? (
                <div className="mt-4 flex items-center gap-2">
                  {uploadLivenessFile.isPending && (
                    <ClipLoader size={20} color="#0B75C2" />
                  )}
                  {livenessUploaded && (
                    <span className="text-[12px] text-green-600 dark:text-green-400">
                      Uploaded
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setLivenessFile(null);
                      setLivenessUploaded(false);
                    }}
                    className="text-red-600 dark:text-red-400 text-[12px] font-medium"
                  >
                    Retake
                  </button>
                </div>
              ) : (
                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      getLivenessUrl.mutate(undefined);
                      setShowLivenessWebcam(true);
                    }}
                    className="flex-1 h-[44px] rounded-xl bg-secondary_button text-white text-[14px] font-medium"
                  >
                    Take selfie
                  </button>
                  <label className="flex-1 h-[44px] rounded-xl border border-gray-300 dark:border-gray-600 flex items-center justify-center cursor-pointer text-[14px] text-gray-700 dark:text-gray-300">
                    <input
                      type="file"
                      accept={ACCEPT_IMAGE_ONLY}
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = "";
                        if (!f) return;
                        if (!isAllowedDocument(f, false)) {
                          toast.error(
                            "Please upload an image (PNG, JPG, etc.) only."
                          );
                          return;
                        }
                        handleLivenessFileSelect(f);
                      }}
                    />
                    Upload photo
                  </label>
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || submitLevel2.isPending}
            className="mt-8 w-full h-[52px] rounded-xl bg-secondary_button text-white font-semibold text-[15px] disabled:opacity-50 flex items-center justify-center"
          >
            {submitLevel2.isPending ? (
              <ClipLoader color="#FFFFFF" size={28} />
            ) : (
              "Submit for Review"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Level2Flow;
